// Shared presentational components. Status is always stated in text, never by colour alone.
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { ApiRequestError, isDemoReadOnly } from '../api.ts';
import { useI18n } from '../i18n.tsx';
import { iconPath } from '../nav.ts';

export function Icon({ name }: { name: string }) {
  return (
    <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={iconPath(name)} />
    </svg>
  );
}

export function Brand() {
  const { t } = useI18n();
  return (
    <>
      {/* Exact Scinops emblem (SHA-256 verified in tests); never recreated, recoloured or mirrored. */}
      <img src="/scinops-logo.svg" alt="Scinops" />
      <div>
        <strong lang="en">{t('app.name')}</strong>
        <small dir="auto">{t('app.byline')}</small>
      </div>
    </>
  );
}

const STATUS_TONE: Record<string, string> = { active: 'green', inactive: '', disabled: 'red', invited: 'blue', suspended: 'red', succeeded: 'green', denied: 'red', failed: 'amber' };

export function StatusBadge({ status }: { status: string }) {
  const { t, has } = useI18n();
  const key = `status.${status}`;
  return <span className={`badge ${STATUS_TONE[status] ?? ''}`}>{has(key) ? t(key) : status}</span>;
}

export function PageHead({ eyebrow, title, intro, actions }: { eyebrow: string; title: string; intro?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="pagehead">
      <div>
        <div className="eyebrow">{eyebrow}</div>
        <h1>{title}</h1>
        {intro ? <p>{intro}</p> : null}
      </div>
      {actions ? <div className="actions">{actions}</div> : null}
    </div>
  );
}

export function Loading({ label }: { label?: string }) {
  const { t } = useI18n();
  return (
    <div className="empty" role="status" aria-live="polite">
      <p>{label ?? t('common.loading')}</p>
    </div>
  );
}

/** Distinguishes denied, missing, network-unknown and other failures for the reader. */
export function errorMessage(e: unknown, t: ReturnType<typeof useI18n>['t']): string {
  if (e instanceof ApiRequestError) return e.code === 'NETWORK' ? t('error.network') : e.message;
  return t('error.requestFailed');
}

/** PD-730 (demo mode): a refused change is explained as information, never as an error page. */
export function DemoReadOnlyNote() {
  const { t } = useI18n();
  return (
    <div className="note demo" role="status">
      <strong>{t('demo.notice.title')}</strong>
      {t('demo.notice.body')}
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const { t } = useI18n();
  if (isDemoReadOnly(error)) return <DemoReadOnlyNote />;
  const e = error instanceof ApiRequestError ? error : null;
  const title = t(e?.status === 403 ? 'error.title.forbidden' : e?.status === 404 ? 'error.title.notFound' : e?.code === 'NETWORK' ? 'error.title.network' : 'error.title.generic');
  return (
    <div className="note danger" role="alert">
      <strong>{title}</strong>
      {errorMessage(error, t)}
      {e?.correlationId ? <Reference id={e.correlationId} /> : null}
      {retry && !(e && (e.status === 403 || e.status === 404)) ? (
        <div style={{ marginBlockStart: 10 }}>
          <button className="button small" type="button" onClick={retry}>
            {t('common.tryAgain')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function labelFor(id: string, fallback: string): string {
  const label = typeof document !== 'undefined' ? document.querySelector(`label[for="${CSS.escape(id)}"]`)?.textContent : null;
  return label?.trim() || fallback;
}

/** Error summary for forms: announced, focusable, links to each invalid field. */
function Reference({ id }: { id: string }) {
  const { t } = useI18n();
  const [before, after] = t('common.reference', { id: '\u0000' }).split('\u0000');
  return (
    <div className="tiny">
      {before}
      <span data-ltr>{id}</span>
      {after}
    </div>
  );
}

export function ErrorSummary({ error, fieldIds }: { error: unknown; fieldIds: Record<string, string> }) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error) ref.current?.focus();
  }, [error]);
  if (!error) return null;
  if (isDemoReadOnly(error)) return <DemoReadOnlyNote />;
  const e = error instanceof ApiRequestError ? error : null;
  const fields = Object.entries(e?.fields ?? {});
  return (
    <div className={`note ${e?.status === 409 ? 'warning' : 'danger'}`} role="alert" tabIndex={-1} ref={ref}>
      <strong>{e ? errorMessage(e, t) : t('error.notSaved')}</strong>
      {fields.length ? (
        <ul>
          {fields.map(([k, msg]) => (
            <li key={k}>{fieldIds[k] ? <a href={`#${fieldIds[k]}`}>{labelFor(fieldIds[k], k)}: {msg}</a> : msg}</li>
          ))}
        </ul>
      ) : null}
      {e?.correlationId ? <Reference id={e.correlationId} /> : null}
    </div>
  );
}

export function Field({ label, hint, error, children, id, full }: { label: string; hint?: string; error?: string | undefined; children: (props: { id: string; 'aria-invalid'?: true; 'aria-describedby'?: string }) => ReactNode; id?: string; full?: boolean }) {
  const auto = useId();
  const fid = id ?? auto;
  const hintId = `${fid}-hint`;
  const errId = `${fid}-error`;
  const describedBy = [hint ? hintId : null, error ? errId : null].filter(Boolean).join(' ');
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label htmlFor={fid}>{label}</label>
      {children({ id: fid, ...(error ? { 'aria-invalid': true as const } : {}), ...(describedBy ? { 'aria-describedby': describedBy } : {}) })}
      {hint ? <small id={hintId}>{hint}</small> : null}
      {error ? (
        <small className="field-error" id={errId}>
          {error}
        </small>
      ) : null}
    </div>
  );
}

export function Dialog({ open, title, subtitle, onClose, children }: { open: boolean; title: string; subtitle?: string; onClose: () => void; children: ReactNode }) {
  const { t } = useI18n();
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} aria-labelledby={titleId} onClose={onClose} onCancel={onClose}>
      <div className="dialog-head">
        <div>
          <h2 id={titleId}>{title}</h2>
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
        <button className="button quiet small" type="button" onClick={onClose} aria-label={t('common.close')}>
          ✕
        </button>
      </div>
      <div className="dialog-body">{open ? children : null}</div>
    </dialog>
  );
}
